import { Object3D, Quaternion, Vector3 } from "three";
import { SugarModel } from "../loader/SugarModel";
import { SugarModelJoint } from "../loader/SugarModelJoint";
import { isConfiguratorRequiredJointType } from "./JointTypes";
import { logger } from "./Logger";
import {
  CONFIGURATOR_DATA,
  CONFIGURATOR_MODEL_TYPE,
  MODEL_PART_TYPE,
} from "../core/configurator/ConfiguratorData";

export const fetchAllJoints = (models: SugarModel[]) => {
  return models
    .map((m) => m.joints)
    .reduce((prev, current) => {
      prev = [...prev!, ...(current ?? [])];
      return prev;
    }, []);
};

export const fetchMinDistance = (
  checkModel: SugarModel,
  otherModels: SugarModel[]
): { distance: number; base: any; tail: any } | undefined => {
  const newJoints = checkModel.joints?.filter((j) => !j.binded);
  if (!newJoints) return undefined;

  const outTypes = newJoints.map((n) => n.outType);
  let otherJoints = fetchAllJoints(otherModels.filter((o) => o != checkModel));
  otherJoints = otherJoints?.filter((oj) => outTypes.includes(oj.inType));
  otherJoints = otherJoints?.filter((oj) => !oj.binded);

  const result = { distance: Infinity, base: {}, tail: {} };
  const prevPos = new Vector3();
  const currentPos = new Vector3();

  let found = false;
  for (let i = 0; i < newJoints!.length; i++) {
    newJoints![i].getWorldPosition(currentPos);

    for (let j = 0; j < otherJoints!.length; j++) {
      otherJoints![j].getWorldPosition(prevPos);
      if (newJoints![i].outType !== otherJoints![j].inType) continue;
      if (!checkExcludes(newJoints[i].model, otherJoints![j].model)) continue;

      const distance = prevPos.distanceTo(currentPos);
      if (distance < result.distance) {
        result.distance = distance;
        result.base = otherJoints![j];
        result.tail = newJoints![i];
        found = true;
      }
    }
  }

  return found ? result : undefined;
};

export const fetchScreenSpaceMinDistance = (
  checkModel: SugarModel,
  otherModels: SugarModel[]
): { distance: number; base: any; tail: any } | undefined => {
  const newJoints = checkModel.joints;
  if (!newJoints) return undefined;

  const outTypes = newJoints.map((n) => n.outType);
  let otherJoints = fetchAllJoints(otherModels.filter((o) => o != checkModel));
  otherJoints = otherJoints?.filter((oj) => outTypes.includes(oj.inType));
  otherJoints = otherJoints?.filter((oj) => !oj.binded);

  const result = { distance: Infinity, base: {}, tail: {} };
  const prevPos = new Vector3();
  const currentPos = new Vector3();

  let found = false;
  for (let i = 0; i < newJoints!.length; i++) {
    newJoints![i].getWorldPosition(currentPos);

    for (let j = 0; j < otherJoints!.length; j++) {
      otherJoints![j].getWorldPosition(prevPos);
      if (newJoints![i].outType !== otherJoints![j].inType) continue;
      if (!checkExcludes(newJoints[i].model, otherJoints![i].model)) continue;
      const distance = prevPos.distanceTo(currentPos);
      if (distance < result.distance) {
        result.distance = distance;
        result.base = otherJoints![j];
        result.tail = newJoints![i];
        found = true;
      }
    }
  }

  return found ? result : undefined;
};

export const findJointByOut = (
  model: SugarModel,
  outType: string,
): SugarModelJoint | undefined =>
  model.joints?.find(
    (j) => !j.binded && j.outType === outType,
  ) ?? model.joints?.find((j) => j.outType === outType);

export const findJointByIn = (
  model: SugarModel,
  inType: string,
): SugarModelJoint | undefined =>
  model.joints?.find(
    (j) => !j.binded && j.inType === inType,
  ) ?? model.joints?.find((j) => j.inType === inType);

export type ExplicitJointSpec = {
  /** Child model joint outType */
  childOut: string;
  /** Parent model joint inType */
  parentIn: string;
};

/** Child.outType ↔ parent.inType (fetchMinDistance kuralı). */
export const bindModelsByJointSpec = (
  parentModel: SugarModel,
  childModel: SugarModel,
  spec: ExplicitJointSpec,
): { base: SugarModelJoint; tail: SugarModelJoint } | undefined => {
  logger.warn("bindModelsByJointSpec", {
    childOut: spec.childOut,
    parentIn: spec.parentIn,
    childOutTypes: childModel.joints?.map((j) => j.outType),
    parentInTypes: parentModel.joints?.map((j) => j.inType),
  });

  
  const tail = findJointByOut(childModel, spec.childOut);
  const base = findJointByIn(parentModel, spec.parentIn);
  if (!base || !tail) {
    logger.warn("[JointUtil.bindModelsByJointSpec] joint bulunamadı", {
      childOut: spec.childOut,
      parentIn: spec.parentIn,
      childOutTypes: childModel.joints?.map((j) => j.outType),
      parentInTypes: parentModel.joints?.map((j) => j.inType),
    });
    return undefined;
  }
  alignJoints(base, tail);
  base.bindJoint(tail);
  return { base, tail };
};

/** tail joint base'e oturana kadar tail modeline uygulanacak rotation + translation. */
export type JointSnapDelta = {
  rotation: Quaternion;
  translation: Vector3;
};

const _jointSnapOrigPos = new Vector3();
const _jointSnapOrigQuat = new Quaternion();
const _jointSnapBasePos = new Vector3();
const _jointSnapTailPos = new Vector3();

function jointSnapDirections(
  base: SugarModelJoint,
  tail: SugarModelJoint,
  worldLocal: boolean,
): { rotation: Quaternion; basePosition: Vector3 } | undefined {
  if (base == null || tail == null) {
    logger.warn("[JointUtil] base veya tail yok", {
      hasBase: base != null,
      hasTail: tail != null,
    });
    return undefined;
  }

  const baseDirVec = base.direction;
  const tailDirVec = tail.direction;
  if (!baseDirVec || !tailDirVec) {
    logger.warn("[JointUtil] direction tanımlı değil (joint.apply çağrılmamış olabilir)", {
      baseInType: base.inType,
      baseOutType: base.outType,
      tailInType: tail.inType,
      tailOutType: tail.outType,
      hasBaseDirection: !!baseDirVec,
      hasTailDirection: !!tailDirVec,
    });
    return undefined;
  }

  const baseDirection = baseDirVec.clone();
  if (worldLocal) base.getWorldDirection(baseDirection);

  const tailDirection = tailDirVec.clone();
  if (worldLocal) tail.getWorldDirection(tailDirection);
  tailDirection.multiplyScalar(-1);

  const rotation = new Quaternion().setFromUnitVectors(tailDirection, baseDirection);

  const basePosition = base.offset.clone();
  if (worldLocal) base.getWorldPosition(basePosition);

  return { rotation, basePosition };
}

/**
 * tail joint → base joint hizası için delta.
 * Varsayılan uygulama hedefi: tail.getModel(); başka Object3D'ye de uygulanabilir.
 */
export function computeSnapTailToBaseDelta(
  base: SugarModelJoint,
  tail: SugarModelJoint,
  worldLocal = true,
): JointSnapDelta | undefined {
  const dirs = jointSnapDirections(base, tail, worldLocal);
  const tailModel = tail.getModel();
  if (!dirs || !tailModel) return undefined;

  _jointSnapOrigPos.copy(tailModel.position);
  _jointSnapOrigQuat.copy(tailModel.quaternion);

  tailModel.applyQuaternion(dirs.rotation);
  tailModel.updateMatrixWorld(true);

  _jointSnapTailPos.copy(tail.offset);
  if (worldLocal) tail.getWorldPosition(_jointSnapTailPos);
  else _jointSnapTailPos.applyQuaternion(dirs.rotation);

  const translation = dirs.basePosition.clone().sub(_jointSnapTailPos);

  tailModel.position.copy(_jointSnapOrigPos);
  tailModel.quaternion.copy(_jointSnapOrigQuat);
  tailModel.updateMatrixWorld(true);

  return { rotation: dirs.rotation.clone(), translation };
}

/** base joint → tail joint hizası (argüman sırası aynı, hareket eden model base.getModel()). */
export function computeSnapBaseToTailDelta(
  base: SugarModelJoint,
  tail: SugarModelJoint,
  worldLocal = true,
): JointSnapDelta | undefined {
  return computeSnapTailToBaseDelta(tail, base, worldLocal);
}

export function applyJointSnapDelta(
  target: Object3D,
  delta: JointSnapDelta,
): void {
  target.applyQuaternion(delta.rotation);
  target.updateMatrixWorld(true);
  target.position.add(delta.translation);
  target.updateMatrixWorld(true);
}

export const alignJoints = (
  base: SugarModelJoint,
  tail: SugarModelJoint,
  worldLocal = true,
) => {
  const delta = computeSnapTailToBaseDelta(base, tail, worldLocal);
  const model = tail.getModel();
  if (!delta || !model) return;
  applyJointSnapDelta(model, delta);
};

export const realignJoints = (
  model: SugarModel,
  ...excludeBindedJoints: SugarModelJoint[]
) => {
  const bindedJoints = model.joints?.filter((j) => j.binded && !j.freeze);
  bindedJoints?.forEach((joint) => {
    alignJoints(joint, joint.bindedJoint!);
    if (!excludeBindedJoints.includes(joint.bindedJoint!)) {
      excludeBindedJoints.push(joint.bindedJoint!);
      realignJoints(joint.bindedJoint!.model!, ...excludeBindedJoints);
    }
  });
};

export const checkExcludes = (baseModel: SugarModel, tailModel: SugarModel) => {
  
  const baseJointInfo = baseModel?.userData?.info?.jointInfo;
  const tailJointInfo = tailModel?.userData?.info?.jointInfo;

  if (!baseJointInfo || !tailJointInfo) return true;

  const baseId = baseJointInfo.id;
  const tailId = tailJointInfo.id;
  const baseExcludes: any[] = baseJointInfo.excludes ?? [];
  const tailExcludes: any[] = tailJointInfo.excludes ?? [];

  return !baseExcludes.includes(tailId) && !tailExcludes.includes(baseId);
};

export const getPossibleBindableJoints = (model: SugarModel) => {
  const bindedJoints = model.joints?.filter((j) => j.binded && !j.freeze) || [];
  return (
    bindedJoints.flatMap((leftJ) => {
      return bindedJoints
        .filter(
          (r) =>
            r.bindedJoint &&
            r.bindedJoint != leftJ &&
            r.bindedJoint?.model != leftJ.model &&
            r.bindedJoint?.inType === leftJ.outType &&
            isConfiguratorRequiredJointType(r.bindedJoint?.inType)
        )
        .map((r) => {
          return r.bindedJoint!;
        });
    }) || []
  );
};

export const getJointDataFromModelData = (
  partOption: MODEL_PART_TYPE,
  baseModelData: any,
  configData?: CONFIGURATOR_MODEL_TYPE,
): { tailModelData: any; baseJoint: any } => {
  const models = (configData ?? CONFIGURATOR_DATA).models;
  return baseModelData.jointInfo.modelJoints
    .filter((joint: any) => joint.outType === partOption.type)
    .map((joint: any) => {
      const tailModelData = models.find(
        (m) => m.id == getPartTypeMatch(partOption, baseModelData.id)
      );
      return {
        tailModelData: tailModelData,
        baseJoint: joint,
      };
    });
};

export const getPartTypeMatch = (part: MODEL_PART_TYPE, id: number) => {
  if (part.match)
    return typeof part.match === "number" ? part.match : part.match[id];
  if (part.joints) {
    const p = part.joints.find((jj) =>
      typeof jj.match === "number" ? jj.match : jj.match[id]
    );

    return typeof p?.match === "number" ? p.match : p?.match[id];
  }
};
